import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, filter } from 'rxjs/operators';
import { Permission, PermissionGroup, PermissionType } from 'src/app/core/entity/permission';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { PermissionService } from 'src/app/core/services/permission.service';
import { ToastActions } from 'src/app/core/state/toast/toast.actions';
import { UserType } from 'src/app/core/state/types';
import { UserSelectors } from 'src/app/core/state/users/users.selectors';
import { MultiselectWithAllDropdownComponent } from '../../../multiselect-dropdown/multiselect-with-all-dropdown.component';
import { PermissionRootState, PermissionState, PermissionStateFacade } from './permission-state.facade';
import { TranslatableStringType } from 'src/app/core/i18n/types';

interface PermissionSelectorComponentTranslations {
  showClientInNotificationTab: TranslatableStringType;
  noOne: TranslatableStringType;
  selectUser: TranslatableStringType;
  showTaxAccountFor: TranslatableStringType;
  all: TranslatableStringType;
  firmAdmin: TranslatableStringType;
  firmAdminAndResponsibleUser: TranslatableStringType;
  saved: TranslatableStringType;
  permissionsHasBeenUpdated: TranslatableStringType;
}

@Component({
  selector: 'app-permission-selector',
  templateUrl: './permission-selector.component.html',
  providers: [PermissionStateFacade, getTranslationProvider('permissionSelectorComponent')],
})
export class PermissionSelectorComponent extends TranslatableComponent {
  @ViewChild('visibleInNotificationMultiselect') visibleInNotificationMultiselect: MultiselectWithAllDropdownComponent;
  @ViewChild('showTaxAccountMultiselect') showTaxAccountMultiselect: MultiselectWithAllDropdownComponent;

  @Input()
  get clientId() {
    return this._clientId;
  }
  set clientId(clientId: number | undefined) {
    this._clientId = clientId;
    if (!clientId || clientId < 1) {
      this.clearPermissionState();
      return;
    }
    this.loadAndUpdatePermissionState();
  }

  @Input() disabled = true;
  @Input() disabledTooltip: string | undefined;
  @Output() change: EventEmitter<Permission[]> = new EventEmitter<Permission[]>();

  get VisibleInNotification() {
    return PermissionType.VisibleInNotification;
  }
  get ShowTaxAccountBalance() {
    return PermissionType.ShowTaxAccountBalance;
  }

  availableUsers$: Observable<UserType[]>;
  permissionState$: Observable<PermissionRootState>;
  permissionGroupOptions: { name: string; value: PermissionGroup }[] = [];
  i18n: PermissionSelectorComponentTranslations;

  private permissionGroupOptionsTemplate = [
    { name: 'all', value: PermissionGroup.All },
    { name: 'firmAdmin', value: PermissionGroup.OnlyFirmAdmin },
    { name: 'firmAdminAndResponsibleUser', value: PermissionGroup.OnlyResponsibleUserAndFirmAdmin },
    { name: 'selectUser', value: PermissionGroup.OnlySelectedUsers },
  ];

  private onSaveSubject = new BehaviorSubject<PermissionType | null>(null);
  private _clientId: number | undefined;

  constructor(
    private permissionState: PermissionStateFacade,
    private permissionService: PermissionService,
  ) {
    super();
    this.i18n = {} as PermissionSelectorComponentTranslations;
    this.permissionState$ = this.permissionState.createRootStateObservable();
    this.availableUsers$ = this.store.select(UserSelectors.activeUsersWithoutAll);
    this.setupSaveSubscription();
  }

  protected override onTranslationsLoaded() {
    this.i18n = {
      showClientInNotificationTab: this.translate('showClientInNotificationTab'),
      noOne: this.translate('noOne'),
      selectUser: this.translate('selectUser'),
      showTaxAccountFor: this.translate('showTaxAccountFor'),
      all: this.translate('all'),
      firmAdmin: this.translate('firmAdmin'),
      firmAdminAndResponsibleUser: this.translate('firmAdminAndResponsibleUser'),
      saved: this.translate('saved'),
      permissionsHasBeenUpdated: this.translate('permissionsHasBeenUpdated'),
    };

    this.permissionGroupOptions = this.permissionGroupOptionsTemplate.map((option) => ({
      name: this.i18n[option.name],
      value: option.value,
    }));
  }

  private clearPermissionState() {
    this.permissionState.initState([]);
  }

  private loadAndUpdatePermissionState() {
    this.permissionService.loadPermissions(this.clientId).subscribe((permissions: Permission[]) => {
      this.permissionState.initState(permissions);
    });
  }

  showVisibleInNotificationUsersDropdown() {
    setTimeout(() => {
      this.visibleInNotificationMultiselect.show();
    }, 20); // wait for the angular change detection to show the dropdown
  }

  showTaxAccountBalanceUsersDropdown() {
    setTimeout(() => {
      this.showTaxAccountMultiselect.show();
    }, 20); // wait for the angular change detection to show the dropdown
  }

  onMultiselectHide(permissionType: PermissionType) {
    this.permissionState.clearIfEmpty(permissionType);
    this.onSaveSubject.next(permissionType);
  }

  updateNotificationPermissionState(prop: string, value: any, persistChanges = true) {
    this.updateState(PermissionType.VisibleInNotification, prop, value);
    if (prop === 'group' && value === PermissionGroup.OnlySelectedUsers) {
      this.showVisibleInNotificationUsersDropdown();
      return;
    }
    if (persistChanges) {
      this.onSaveSubject.next(PermissionType.VisibleInNotification);
    }
  }

  updateTaxAccountPermissionState(prop: any, value: any, persistChanges = true) {
    this.updateState(PermissionType.ShowTaxAccountBalance, prop, value);
    if (prop === 'group' && value === PermissionGroup.OnlySelectedUsers) {
      this.showTaxAccountBalanceUsersDropdown();
      return;
    }
    if (persistChanges) {
      this.onSaveSubject.next(PermissionType.ShowTaxAccountBalance);
    }
  }

  private updateState(permissionType: PermissionType, prop: string, value: any) {
    let updatedState: Partial<PermissionState> = { [prop]: value };

    if (prop === 'active') {
      updatedState = {
        active: value,
        group: value ? PermissionGroup.OnlyResponsibleUserAndFirmAdmin : PermissionGroup.None,
        users: [],
      };
    }
    if (prop === 'group') {
      updatedState.users = [];
    }

    this.permissionState.updateState(permissionType, updatedState);
  }

  private setupSaveSubscription() {
    this.onSaveSubject
      .pipe(
        filter((permissionType) => !!permissionType),
        this.permissionState.filterUnchanged(),
        this.permissionState.withLatestFromState(),
        this.takeUntilDestroyed(),
        concatMap(([permissionType, state]: [PermissionType, PermissionState]) =>
          this.savePermissions(permissionType, state),
        ),
        catchError((err: unknown) => {
          // eslint-disable-next-line no-console
          console.error('Error saving permissions', 'client id: ' + this._clientId, err);
          return throwError(() => err);
        }),
      )
      .subscribe((response) => {
        if (!response) {
          this.permissionState.rollbackToPreviousPersistedState();
          return;
        }

        this.permissionState.initState(response);
        this.store.dispatch(
          ToastActions.showInfoMessage({
            summary: this.i18n.saved,
            detail: this.i18n.permissionsHasBeenUpdated,
          }),
        );
        this.change.emit(response);
      });
  }

  private savePermissions(permissionType: PermissionType, state: PermissionState) {
    return this.permissionService
      .updatePermission(this.clientId, permissionType, state.group, state.users)
      .pipe(catchError(() => of(null)));
  }
}
